import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  OnboardingSurveyCardsConfig,
  OnboardingSurveyCardId,
  buildEnabledSurveyQueue,
  getOnboardingSurveyCard,
} from "../../../configs/OnboardingSurveyCardsConfig";
import { zeosDialog } from "../../../components/Shared/confirm-dialog/ConfirmDialog";
import {
  clearSurveyHubSelection,
  dequeueSurvey,
  selectedSurveyIdsSelector,
  setSelectedSurveyIds,
  setSurveyQueue,
} from "../../../redux-store/OnboardingSlice";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { isDemo } from "../../../services/environment/EnvironmentHelpers";
import { createLogger } from "../../../services/Logger";
import { IOnboardStatus } from "../../../services/nav/Interfaces";
import { NavigationService } from "../../../services/nav/NavigationService";
import { OnboardContainer } from "../layout/OnboardContainer";

const log = createLogger("SurveysHub");

export const SurveysHubScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedSurveyIds = useSelector(selectedSurveyIdsSelector);

  const [zeosUser, setZeosUser] = useState<IZeosUser>();

  const handleUserCallback = (user: IZeosUser): void => {
    setZeosUser(user);
  };

  const handleStatusCallback = (_status: IOnboardStatus): void => {
    // Hub does not branch on back/forward beyond Redux hydrate.
  };

  const selectedSet = useMemo(
    () => new Set(selectedSurveyIds),
    [selectedSurveyIds]
  );

  const enabledSelectedIds = useMemo(
    () => buildEnabledSurveyQueue(selectedSurveyIds),
    [selectedSurveyIds]
  );

  const estimatedMinutes = useMemo(
    () =>
      enabledSelectedIds.reduce((sum, id) => {
        const card = getOnboardingSurveyCard(id);
        return sum + (card?.estimatedMinutes ?? 0);
      }, 0),
    [enabledSelectedIds]
  );

  const toggleCard = useCallback(
    (id: OnboardingSurveyCardId, enabled: boolean) => {
      if (!enabled) {
        return;
      }
      const next = selectedSet.has(id)
        ? selectedSurveyIds.filter((x) => x !== id)
        : [...selectedSurveyIds, id];
      dispatch(setSelectedSurveyIds(next));
    },
    [dispatch, selectedSet, selectedSurveyIds]
  );

  const goPastSurveys = useCallback(() => {
    if (isDemo()) {
      navigate(NavigationService.buildOnboardingUrl("summary"));
      return;
    }
    navigate(NavigationService.buildOnboardingUrl("uapi"));
  }, [navigate]);

  const handleSkip = useCallback(() => {
    log.info("Skip for Now — clearing survey hub selection", {
      zeosUserId: zeosUser?.zeosUserId,
    });
    dispatch(clearSurveyHubSelection());
    goPastSurveys();
  }, [dispatch, goPastSurveys, zeosUser?.zeosUserId]);

  const startQueueHead = useCallback(
    (queue: OnboardingSurveyCardId[]) => {
      if (queue.length === 0) {
        goPastSurveys();
        return;
      }

      const head = queue[0];
      const card = getOnboardingSurveyCard(head);
      if (!card || !card.enabled || !card.route) {
        zeosDialog("That survey is not available yet.");
        return;
      }

      // Soft handoff for Explore: leave onboarding; drop this card from selection/queue.
      if (card.kind === "explore") {
        dispatch(setSurveyQueue(queue.slice(1)));
        dispatch(dequeueSurvey(head));
        navigate(card.route);
        return;
      }

      dispatch(setSurveyQueue(queue));
      navigate(card.route);
    },
    [dispatch, goPastSurveys, navigate]
  );

  const handleStartSelected = useCallback(
    (doNavigate: () => void) => {
      void doNavigate;
      if (enabledSelectedIds.length === 0) {
        zeosDialog("Select at least one available survey, or Skip for Now.");
        return;
      }
      startQueueHead(enabledSelectedIds);
    },
    [enabledSelectedIds, startQueueHead]
  );

  const content = (
    <div className="flex flex-col gap-6 font-sans">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-lg font-semibold text-[#333]">
          Which areas would you like to explore?
        </p>
        <p className="text-sm text-[#8a8a8a]">
          Select all that interest you — each survey takes just a few minutes.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {OnboardingSurveyCardsConfig.map((card) => {
          const selected = selectedSet.has(card.id);
          const interactive = card.enabled;
          return (
            <button
              key={card.id}
              type="button"
              disabled={!interactive}
              onClick={() => toggleCard(card.id, card.enabled)}
              className={[
                "relative text-left rounded-xl border p-5 min-h-[180px] flex flex-col gap-3 transition-colors",
                selected
                  ? "border-2 border-zeos-orange-dk bg-[#FEF3E8]"
                  : "border-[#dcdcdc] bg-white",
                interactive
                  ? "cursor-pointer hover:border-[#c3c3c3]"
                  : "cursor-not-allowed opacity-60",
              ].join(" ")}
              aria-pressed={selected}
            >
              <card.icon
                className="w-6 h-6 text-zeos-orange-dk"
                aria-hidden
              />
              <span
                className={[
                  "absolute top-4 right-4 w-5 h-5 rounded border-2",
                  selected
                    ? "border-zeos-orange-dk bg-zeos-orange-dk"
                    : "border-[#c9c9c9] bg-white",
                ].join(" ")}
                aria-hidden
              />
              <h3 className="text-[17px] font-bold text-[#333] pr-8 leading-snug">
                {card.title}
              </h3>
              <p className="text-sm text-[#555] leading-relaxed flex-1">
                {card.blurb}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-[#8a8a8a] bg-[#f1f1f1] rounded-full px-3 py-1">
                  ~{card.estimatedMinutes} min
                </span>
                {!card.enabled && (
                  <span className="text-xs font-bold text-[#2B3990] bg-[#eceefb] rounded-full px-3 py-1">
                    Coming soon
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#f7f7f6] px-5 py-4">
        <p className="text-[15px] font-bold text-[#333]">
          <span className="text-zeos-orange-dk">
            {enabledSelectedIds.length} of{" "}
            {OnboardingSurveyCardsConfig.filter((c) => c.enabled).length}
          </span>{" "}
          available surveys selected
          {estimatedMinutes > 0
            ? `  ·  Estimated time: ${estimatedMinutes} minutes`
            : ""}
        </p>
        <p className="text-sm text-[#8a8a8a]">
          Your responses power smarter recommendations and incentive matches
          across ZEOS-AI.
        </p>
      </div>
    </div>
  );

  return (
    <OnboardContainer
      content={content}
      contentType="display"
      pageSlug="surveys"
      onNext={handleStartSelected}
      onSkip={handleSkip}
      showSkipButton
      skipLabel="Skip for Now"
      continueLabel="Start Selected Surveys"
      isContinueDisabled={enabledSelectedIds.length === 0}
      userCallback={handleUserCallback}
      statusCallback={handleStatusCallback}
    />
  );
};
